import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getCookie } from "../../utility/getCookie";
import {
  Address,
  AddressState,
  CreateAddressRequest,
  UpdateAddressRequest,
  AddressResponse,
} from "./types";

const API_URL = "https://devapi.salar.in/api/v1/customer";

// Axios instance with cookies support
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add Bearer token
api.interceptors.request.use(
  (config) => {
    const raw = localStorage.getItem("access_token") || getCookie("access_token");
    const token = raw && raw !== "undefined" && raw !== "null" && raw.length > 20 ? raw : null;
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Async Thunks
export const addAddress = createAsyncThunk(
  "customerAddress/addAddress",
  async (data: CreateAddressRequest, { rejectWithValue }) => {
    try {
      const response = await api.post<AddressResponse>(
        "/customer-address",
        data
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add address"
      );
    }
  }
);

export const getAllAddresses = createAsyncThunk(
  "customerAddress/getAllAddresses",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<AddressResponse>("/customer-address");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch addresses"
      );
    }
  }
);

export const getAddressById = createAsyncThunk(
  "customerAddress/getAddressById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.get<AddressResponse>(
        `/customer-address/${id}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch address"
      );
    }
  }
);

export const updateAddress = createAsyncThunk(
  "customerAddress/updateAddress",
  async (
    { id, data }: { id: string; data: UpdateAddressRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch<AddressResponse>(
        `/customer-address/${id}`,
        data
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update address"
      );
    }
  }
);

export const deleteAddress = createAsyncThunk(
  "customerAddress/deleteAddress",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.delete<AddressResponse>(
        `/customer-address/${id}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete address"
      );
    }
  }
);

export const setDefaultAddress = createAsyncThunk(
  "customerAddress/setDefaultAddress",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.patch<AddressResponse>(
        `/customer-address/${id}/set-default`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to set default address"
      );
    }
  }
);

const initialState: AddressState = {
  addresses: [],
  currentAddress: null,
  loading: false,
  error: null,
  success: false,
  message: null,
};

const addressSlice = createSlice({
  name: "customerAddress",
  initialState,
  reducers: {
    clearAddressState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.message = null;
    },
    clearCurrentAddress: (state) => {
      state.currentAddress = null;
    },
    setCurrentAddress: (state, action: PayloadAction<Address>) => {
      state.currentAddress = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Add Address
      .addCase(
        addAddress.fulfilled,
        (state, action: PayloadAction<AddressResponse>) => {
          state.loading = false;
          state.success = true;
          state.message =
            action.payload.message || "Address added successfully";
          state.addresses = action.payload.data as Address[];
        }
      )
      // Get All Addresses
      .addCase(
        getAllAddresses.fulfilled,
        (state, action: PayloadAction<AddressResponse>) => {
          state.loading = false;
          state.addresses = action.payload.data as Address[];
        }
      )
      // Get Address By ID
      .addCase(
        getAddressById.fulfilled,
        (state, action: PayloadAction<AddressResponse>) => {
          state.loading = false;
          state.currentAddress = action.payload.data as Address;
        }
      )
      // Update Address
      .addCase(
        updateAddress.fulfilled,
        (state, action: PayloadAction<AddressResponse>) => {
          state.loading = false;
          state.success = true;
          state.message =
            action.payload.message || "Address updated successfully";
          state.addresses = action.payload.data as Address[];
        }
      )
      // Delete Address
      .addCase(
        deleteAddress.fulfilled,
        (state, action: PayloadAction<AddressResponse>) => {
          state.loading = false;
          state.success = true;
          state.message =
            action.payload.message || "Address deleted successfully";
          state.addresses = action.payload.data as Address[];
        }
      )
      // Set Default Address
      .addCase(
        setDefaultAddress.fulfilled,
        (state, action: PayloadAction<AddressResponse>) => {
          state.loading = false;
          state.success = true;
          state.message =
            action.payload.message || "Default address updated successfully";
          state.addresses = action.payload.data as Address[];
        }
      )
      // Common Matchers — scoped to this slice's thunks only; an app-wide
      // */pending would leave `loading` stuck true (disabling Save) and
      // clobber `success` before the form's effect can observe it.
      .addMatcher(
        (action) =>
          action.type.startsWith("customerAddress/") &&
          action.type.endsWith("/pending"),
        (state) => {
          state.loading = true;
          state.error = null;
          state.success = false;
        }
      )
      .addMatcher(
        (action) =>
          action.type.startsWith("customerAddress/") &&
          action.type.endsWith("/rejected"),
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.success = false;
        }
      );
  },
});

export const { clearAddressState, clearCurrentAddress, setCurrentAddress } =
  addressSlice.actions;
export default addressSlice.reducer;
